import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Search,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Filter,
} from "lucide-react";
import {
  getAllAuditLogs,
  getDoctorAllPatientAuditLogs,
  exportAuditLogs,
  AuditFilters,
} from "../services/auditService";
import { useAuth } from "../contexts/AuthContext";
import CustomSelect from "../components/Common/CustomSelect";
import { toast } from "react-toastify";
import { paginationPageWindow } from "../utils/pagination";

/* ---------- Types ---------- */
interface FieldChange {
  field: string;
  label: string;
  previous: any;
  updated: any;
}

interface AuditLog {
  id: string;
  action?: string | null;
  title: string;
  actorName?: string | null;
  actorRole?: string | null;
  targetName?: string | null;
  entity?: string | null;
  entityId?: string | null;
  subtitle?: string;
  metadata?: FieldChange[] | null;
  status: boolean;
  createdAt: string;
}

/* ---------- Option lists ---------- */
const ENTITY_OPTIONS = [
  "DOCTOR",
  "PATIENT",
  "HOSPITAL",
  "IMAGING",
  "HISTOLOGY_REPORT",
  "CLINICAL_NOTE",
  "MDT_REPORT",
  "MDT_CASE",
  "TREATMENT_HISTORY",
  "MEDICAL_DOCUMENT",
  "USER",
];

const DOCTOR_ENTITY_OPTIONS = [
  "PATIENT",
  "CLINICAL_NOTE",
  "IMAGING",
  "HISTOLOGY_REPORT",
  "MDT_REPORT",
  "TREATMENT_HISTORY",
  "MEDICAL_DOCUMENT",
  "MDT_CASE",
  "USER",
];

const ACTION_OPTIONS = ["CREATE", "UPDATE", "DELETE"];

const ROLE_OPTIONS = ["SUPERADMIN", "DOCTOR"];

/* ---------- Formatters ---------- */
const formatDateTime = (dateString: string) => {
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";

  const pad = (n: number) => String(n).padStart(2, "0");
  const day = pad(d.getDate());
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${day}-${month}-${year} ${time}`;
};

const formatDateDisplay = (iso: string): string => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
};

const formatRole = (role?: string | null) => {
  if (!role) return "-";
  if (role.toUpperCase() === "SUPERADMIN") return "Platform Admin";
  return role
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const formatAction = (
  title?: string | null,
  entity?: string | null,
  action?: string | null
) => {
  if (action) {
    const normalizedAction = action.toUpperCase();
    if (normalizedAction === "UPDATE") return "Update";
    if (normalizedAction === "DELETE") return "Delete";
    if (normalizedAction === "CREATE") return "Create";
  }
  const t = (title || "").toLowerCase();
  if (t.includes("create") || t.includes("onboarded")) return "Create";
  if (t.includes("delete")) return "Delete";
  if (t.includes("update") || t.includes("block") || t.includes("unblock") || t.includes("status changed"))
    return "Update";
  return entity || "Update";
};

const formatFieldName = (name: string) => {
  if (!name) return "";
  if (/^Section (added|removed): /i.test(name) || /^Section ".+": /i.test(name)) return name;
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const formatObjectAsText = (obj: Record<string, any>): string => {
  return Object.entries(obj)
    .map(([k, v]) => {
      const label = k
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (s) => s.toUpperCase())
        .trim();

      let display: string;
      if (v === null || v === undefined || v === "") {
        display = "(empty)";
      } else if (typeof v === "boolean") {
        display = v ? "Yes" : "No";
      } else if (Array.isArray(v)) {
        display = v.length === 0 ? "(empty)" : v.join(", ");
      } else if (typeof v === "object") {
        display = formatObjectAsText(v);
      } else {
        display = String(v);
      }
      return `${label}: ${display}`;
    })
    .join("  ·  ");
};

const formatVal = (val: any): string => {
  if (val === null || val === undefined || val === "") return "—";

  let parsed = val;
  if (typeof val === "string" && (val.startsWith("{") || val.startsWith("["))) {
    try {
      parsed = JSON.parse(val);
    } catch {
      return val;
    }
  }

  if (typeof parsed === "object" && parsed !== null) {
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return "(empty)";
      return parsed
        .map((item: any) =>
          typeof item === "object" && item !== null
            ? formatObjectAsText(item)
            : String(item)
        )
        .join(", ");
    }
    return formatObjectAsText(parsed);
  }

  if (typeof parsed === "boolean") return parsed ? "Yes" : "No";

  return String(parsed);
};

const ITEMS_PER_PAGE = 10;

/* ---------- Action Badge ---------- */
const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
  let classes = "bg-gray-100 text-gray-700 border-gray-200";
  const act = (action || "").toLowerCase();

  if (act === "create") {
    classes = "bg-emerald-50 text-emerald-700 border-emerald-200";
  } else if (act === "update") {
    classes = "bg-amber-50 text-amber-700 border-amber-200";
  } else if (act === "delete") {
    classes = "bg-rose-50 text-rose-700 border-rose-200";
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium border ${classes}`}
    >
      {action}
    </span>
  );
};

/* ---------- Component ---------- */
const AuditActivityPage: React.FC = () => {
  const { user } = useAuth();
  const isDoctor = user?.role === "DOCTOR";

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});

  const toggleRowExpand = (id: string) => {
    setExpandedRowIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /* ---------- Filters (draft + apply) ---------- */
  const [filters, setFilters] = useState<AuditFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [draft, setDraft] = useState({
    search: "",
    dateFrom: "",
    dateTo: "",
    performedBy: "",
    userRole: "all",
    entityType: "all",
    action: "all",
    fieldName: "",
  });

  const setDraftField = (key: keyof typeof draft, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const buildFilters = (d: typeof draft): AuditFilters => {
    const next: AuditFilters = {};
    if (d.search.trim() !== "") next.search = d.search.trim();
    if (d.dateFrom) next.dateFrom = d.dateFrom;
    if (d.dateTo) next.dateTo = d.dateTo;
    if (d.performedBy.trim() !== "")
      next.performedBy = d.performedBy.trim();
    if (d.userRole && d.userRole !== "all") next.userRole = d.userRole;
    if (d.entityType && d.entityType !== "all")
      next.entityType = d.entityType;
    if (d.action && d.action !== "all") next.action = d.action;
    if (d.fieldName.trim() !== "") next.fieldName = d.fieldName.trim();
    return next;
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setFilters(buildFilters(draft));
  };

  const clearFilters = () => {
    setDraft({
      search: "",
      dateFrom: "",
      dateTo: "",
      performedBy: "",
      userRole: "all",
      entityType: "all",
      action: "all",
      fieldName: "",
    });
    setCurrentPage(1);
    setFilters({});
  };

  const [exporting, setExporting] = useState<"pdf" | "xlsx" | null>(null);

  const handleExport = async (format: "pdf" | "xlsx") => {
    if (exporting) return;
    setExporting(format);
    try {
      await exportAuditLogs(format, filters);
      toast.success(
        format === "pdf"
          ? "Audit logs exported as PDF"
          : "Audit logs exported as Excel"
      );
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Failed to export audit logs");
    } finally {
      setExporting(null);
    }
  };

  /* ---------- Fetch Logs ---------- */
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        let data: AuditLog[] = [];
        if (isDoctor) {
          data = await getDoctorAllPatientAuditLogs(
            filters as AuditFilters & { entityType?: string }
          );
        } else {
          data = await getAllAuditLogs(filters);
        }
        setAuditLogs(Array.isArray(data) ? data : []);
        setCurrentPage(1);
      } catch (err) {
        console.error("Failed to load audit logs", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters]);

  /* ---------- Pagination ---------- */
  const totalPages = Math.max(1, Math.ceil(auditLogs.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedLogs = auditLogs.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  /* ---------- Render ---------- */
  return (
    <div className="space-y-6">
      {/* Header with Export Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-4">
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <button
            onClick={() => handleExport("pdf")}
            disabled={exporting !== null}
            className="flex items-center gap-2 h-11 bg-primary-600 text-white px-5 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors shrink-0 disabled:opacity-50"
          >
            {exporting === "pdf" ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FileText size={20} />
            )}
            Export PDF
          </button>
          <button
            onClick={() => handleExport("xlsx")}
            disabled={exporting !== null}
            className="flex items-center gap-2 h-11 bg-primary-600 text-white px-5 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors shrink-0 disabled:opacity-50"
          >
            {exporting === "xlsx" ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FileSpreadsheet size={20} />
            )}
            Export Excel
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-4 sm:p-5 space-y-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters();
          }}
          className="flex items-center gap-3"
        >
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={draft.search}
              onChange={(e) => setDraftField("search", e.target.value)}
              placeholder="Search by actor, entity, description, changed field..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-800 placeholder-gray-400"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="flex items-center justify-center gap-2 px-4 h-10 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-colors shrink-0"
          >
            <Filter size={16} />
            <span>Filters</span>
          </button>
        </form>

        {showFilters && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              applyFilters();
            }}
            className="pt-4 border-t border-gray-100 space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Date From
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={draft.dateFrom}
                    onChange={(e) => setDraftField("dateFrom", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700"
                  />
                  <span
                    className={`pointer-events-none absolute top-px bottom-px left-px right-10 flex items-center pl-3 bg-white text-sm ${
                      draft.dateFrom ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    {draft.dateFrom ? formatDateDisplay(draft.dateFrom) : "DD/MM/YYYY"}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Date To
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={draft.dateTo}
                    onChange={(e) => setDraftField("dateTo", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700"
                  />
                  <span
                    className={`pointer-events-none absolute top-px bottom-px left-px right-10 flex items-center pl-3 bg-white text-sm ${
                      draft.dateTo ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    {draft.dateTo ? formatDateDisplay(draft.dateTo) : "DD/MM/YYYY"}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Performed By
                </label>
                <input
                  type="text"
                  value={draft.performedBy}
                  onChange={(e) => setDraftField("performedBy", e.target.value)}
                  placeholder="Actor name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  User Role
                </label>
                <CustomSelect
                  value={draft.userRole}
                  onChange={(val) => setDraftField("userRole", val)}
                  options={[
                    { value: "all", label: "All Roles" },
                    ...ROLE_OPTIONS.map((r) => ({
                      value: r,
                      label: formatRole(r),
                    })),
                  ]}
                  placeholder="All Roles"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Entity Type
                </label>
                <CustomSelect
                  value={draft.entityType}
                  onChange={(val) => setDraftField("entityType", val)}
                  options={[
                    { value: "all", label: "All Entities" },
                    ...(isDoctor
                      ? DOCTOR_ENTITY_OPTIONS
                      : ENTITY_OPTIONS
                    ).map((e) => ({
                      value: e,
                      label: e,
                    })),
                  ]}
                  placeholder="All Entities"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Action
                </label>
                <CustomSelect
                  value={draft.action}
                  onChange={(val) => setDraftField("action", val)}
                  options={[
                    { value: "all", label: "All Actions" },
                    ...ACTION_OPTIONS.map((a) => ({
                      value: a,
                      label: a,
                    })),
                  ]}
                  placeholder="All Actions"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Field Name
                </label>
                <input
                  type="text"
                  value={draft.fieldName}
                  onChange={(e) => setDraftField("fieldName", e.target.value)}
                  placeholder="e.g., Forename"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              >
                Clear Filters
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">Loading audit logs...</p>
          </div>
        ) : paginatedLogs.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No audit logs found</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your date range or filter options
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50/80 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3.5 text-sm font-bold text-gray-700 whitespace-nowrap">
                      Date &amp; Time
                    </th>
                    <th className="px-6 py-3.5 text-sm font-bold text-gray-700 whitespace-nowrap">
                      Performed By
                    </th>
                    <th className="px-6 py-3.5 text-sm font-bold text-gray-700 whitespace-nowrap">
                      User Role
                    </th>
                    <th className="px-6 py-3.5 text-sm font-bold text-gray-700 whitespace-nowrap">
                      Entity Type
                    </th>
                    <th className="px-6 py-3.5 text-sm font-bold text-gray-700 whitespace-nowrap">
                      Action
                    </th>
                    <th className="px-6 py-3.5 text-sm font-bold text-gray-700">
                      Description
                    </th>
                    <th className="px-6 py-3.5 text-sm font-bold text-gray-700 whitespace-nowrap">
                      Entity ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {paginatedLogs.map((log) => {
                    const hasChanges =
                      Array.isArray(log.metadata) && log.metadata.length > 0;
                    const actionLabel = formatAction(log.title, log.entity, log.action);
                    const isExpanded = !!expandedRowIds[log.id];
                    const target =
                      log.targetName ||
                      (log.entity === "PATIENT" && isDoctor
                        ? log.targetName || log.entityId || "-"
                        : log.entityId || "-");

                    return (
                      <React.Fragment key={log.id}>
                        <tr className="hover:bg-gray-50/80 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-700 whitespace-nowrap">
                            {formatDateTime(log.createdAt)}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                            {log.actorName || "System"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm font-medium bg-blue-50 text-blue-600">
                              {formatRole(log.actorRole)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-800 uppercase tracking-wide whitespace-nowrap">
                            {log.entity || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ActionBadge action={actionLabel} />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 min-w-[180px]">
                            {log.title}
                          </td>
                          <td className="px-6 py-4 text-sm whitespace-nowrap">
                            {hasChanges ? (
                              <button
                                onClick={() => toggleRowExpand(log.id)}
                                className="inline-flex items-center gap-1.5 font-medium text-gray-800 hover:text-emerald-700 transition-colors group cursor-pointer"
                              >
                                <span className="break-all">{target}</span>
                                {isExpanded ? (
                                  <ChevronUp size={15} className="text-emerald-600 shrink-0" />
                                ) : (
                                  <ChevronDown size={15} className="text-emerald-600 shrink-0" />
                                )}
                              </button>
                            ) : (
                              <span className="text-gray-700 break-all font-mono text-sm">
                                {target}
                              </span>
                            )}
                          </td>
                        </tr>

                        {isExpanded && hasChanges && (
                          <tr className="bg-gray-50/60">
                            <td colSpan={7} className="px-6 py-3 border-b border-gray-200">
                              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs">
                                <table className="w-full text-left text-sm">
                                  <thead className="bg-gray-100 text-gray-600 font-bold border-b border-gray-200 uppercase tracking-wider">
                                    <tr>
                                      <th className="px-4 py-2.5 w-1/3">FIELD NAME</th>
                                      <th className="px-4 py-2.5 w-1/3">PREVIOUS VALUE</th>
                                      <th className="px-4 py-2.5 w-1/3">UPDATED VALUE</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {log.metadata?.map((c, i) => (
                                      <tr key={i} className="hover:bg-gray-50/50">
                                        <td className="px-4 py-2.5 font-medium text-gray-900">
                                          {c.label || formatFieldName(c.field)}
                                        </td>
                                        <td className="px-4 py-2.5 text-gray-600">
                                          {formatVal(c.previous)}
                                        </td>
                                        <td className="px-4 py-2.5 text-gray-900 font-semibold">
                                          {formatVal(c.updated)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Showing {startIndex + 1}–
                  {Math.min(currentPage * ITEMS_PER_PAGE, auditLogs.length)} of{' '}
                  {auditLogs.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <div className="flex items-center gap-1">
                    {paginationPageWindow(currentPage, totalPages).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => goToPage(p)}
                        className={`h-8 w-8 rounded-lg text-sm font-medium border flex items-center justify-center transition-colors ${
                          currentPage === p
                            ? "bg-primary-600 border-primary-600 text-white cursor-default"
                            : "bg-white border-gray-200 text-gray-500 hover:bg-primary-50 hover:text-primary-700"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditActivityPage;